import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { sendAnalytics } from '@playcastdotio/Analytics';
import { getPersistentPlayerId } from '../../../api';
import { authStore } from '../../../api/stores/globalStores';
import '../../../styles/aida-base.css';
import '../../../styles/aida-responsive.css';

const WHITE = 'rgba(255, 255, 255, 1)';
const YELLOW = 'rgba(251, 191, 36, 1)';

const CARD_IMAGE =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-09f9aa47-1777-4af5-80ba-166fcccb0bce.png?w=1384&e=webp&nll=true&cX=52&cY=110&cW=765&cH=931';

const NEBULA_LARGE =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/4610144/image-0bdd0551-e329-4b7b-baf5-bbc90abfb745.webp?e=webp&cX=0&cY=0&cW=1438&cH=831';

const NEBULA_SMALL =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/4610144/image-0bdd0551-e329-4b7b-baf5-bbc90abfb745.webp?e=webp&cX=0&cY=5&cW=1440&cH=825';

const textBase: React.CSSProperties = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '10px',
  lineHeight: '17px',
  fontWeight: 400,
  color: WHITE,
  margin: 0,
};

const yellowBold: React.CSSProperties = {
  fontWeight: 700,
  color: YELLOW,
};

const whiteBold: React.CSSProperties = {
  fontWeight: 700,
  color: WHITE,
};

const handleScrollToTeam = () => {
  sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Meet the Team', buttonLocation: 'OurStory' });
  const el = document.getElementById('meet-the-team');
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

export const OurStory = () => {
  const desktopRef = useRef(null);
  const mobileRef = useRef(null);
  const isInView = useInView(desktopRef, { once: true, margin: '-100px' });
  const isMobileInView = useInView(mobileRef, { once: true, amount: 0.1 });

  return (
    <>
      {/* Desktop Layout */}
      <motion.section
        ref={desktopRef}
        className="relative mx-auto hidden w-full lg:block"
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          overflow: 'hidden',
          position: 'relative',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {/* Nebula — top right */}
        <div style={{
          position: 'absolute',
          left: '727px',
          top: '0px',
          width: '605px',
          height: '350px',
          transform: 'rotate(-92deg) scaleX(-1)',
          pointerEvents: 'none',
          zIndex: 1,
        }}>
          <img src={NEBULA_LARGE} alt="" style={{ opacity: 0.43 }} draggable={false} />
        </div>

        {/* Nebula — bottom right */}
        <div style={{
          position: 'absolute',
          left: '716px',
          bottom: '-60px',
          width: '590px',
          height: '338px',
          transform: 'rotate(-92deg)',
          pointerEvents: 'none',
          zIndex: 1,
        }}>
          <img src={NEBULA_SMALL} alt="" style={{ opacity: 0.27 }} draggable={false} />
        </div>

        {/* Content — two-column flex, drives height */}
        <motion.div
          style={{
            position: 'relative',
            zIndex: 10,
            display: 'flex',
            width: '100%',
            padding: '40px 76px 60px',
            boxSizing: 'border-box',
            gap: '20px',
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          {/* Left Column — Card with overlaid title, mission, button */}
          <div style={{
            width: '398px',
            height: '484px',
            flexShrink: 0,
            borderRadius: '19px',
            overflow: 'hidden',
            boxShadow: 'rgba(243, 234, 255, 0.63) 0px 0px 0px 1px inset',
            position: 'relative',
          }}>
            {/* Card Background Image */}
            <img
              src={CARD_IMAGE}
              alt=""
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: 'rotate(180deg)',
                opacity: 0.72,
              }}
            />

            {/* Overlay Content — centered in the card */}
            <div style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '182px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              zIndex: 2,
            }}>
              {/* Our Story Title */}
              <h1 style={{
                lineHeight: '40px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 900,
                fontSize: '40px',
                color: WHITE,
                margin: 0,
                textAlign: 'center',
                fontVariationSettings: "'wght' 900",
              }}>
                Our Story
              </h1>

              {/* Mission Text */}
              <p style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: '10px',
                lineHeight: '12px',
                color: WHITE,
                margin: '8px 0 0 0',
                textAlign: 'center',
                maxWidth: '256px',
              }}>
                Our mission is to make it easy for anyone to play a game the moment they discover it.
              </p>

              {/* Meet the Team Button */}
              <button
                onClick={handleScrollToTeam}
                style={{
                  width: '169px',
                  height: '31px',
                  borderRadius: '30px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '12px',
                  lineHeight: '31px',
                  cursor: 'pointer',
                  marginTop: '16px',
                  color: WHITE,
                  background: 'transparent',
                  border: `1px solid ${WHITE}`,
                }}
              >
                Meet the Team
              </button>
            </div>
          </div>

          {/* Right Column — Story */}
          <div style={{
            flex: 1,
            paddingTop: '22px',
            paddingLeft: '38px',
          }}>
            {/* Meet Playcast. */}
            <h1 style={{
              lineHeight: '33px',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 900,
              fontSize: '33px',
              margin: '0 0 20px 0',
              fontVariationSettings: "'wght' 900",
            }}>
              <span style={{ color: WHITE }}>Meet </span>
              <span style={{ color: YELLOW }}>Playcast.</span>
            </h1>

            {/* Story Text */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
              <p style={textBase}>
                Playcast exists to unify gaming and streaming into an open platform—one that restores
                the imagination, intimacy, and communal creativity that defined classic role-playing
                games long before matchmaking queues and microtransactions replaced table-sized worlds
                and shared storytelling.
              </p>
              <p style={{ ...textBase, marginTop: '17px' }}>
                For years, <span style={yellowBold}>the industry obsessed over the wrong problem</span>.
                Cloud gaming debates fixated on milliseconds of latency while millions of players quietly
                embraced 100% latency—watching instead of playing. The real issue wasn't performance.
                It was platforms. Gamers and developers didn't want another closed ecosystem or another
                credit-card gate. <span style={yellowBold}>They wanted open communities and shared experiences.</span>
              </p>
              <p style={{ ...textBase, marginTop: '17px' }}>
                Instead of adding more centralized infrastructure, Playcast takes the opposite path. It
                achieves unprecedented speed by avoiding the cloud altogether.{' '}
                <span style={whiteBold}>Playcasting is a peer-to-peer network</span> powered by the vast,
                underutilized encoding hardware already sitting inside gaming PCs shipped over the last
                decade; the future hiding in plain sight.
              </p>
              <p style={{ ...textBase, marginTop: '17px' }}>
                During long, dark winters, <span style={yellowBold}>imagination once mattered more than hardware.</span>{' '}
                Stories traveled across tables, not data centers. Playcast is an attempt to carry that
                spirit forward into the twenty-first century—restoring shared storytelling to a world
                that mistook scale for connection.
              </p>
              <p style={{ ...textBase, marginTop: '17px' }}>
                — <span style={whiteBold}>Alex St. John</span>
                <br />
                <span style={{ ...whiteBold, marginLeft: '12px' }}>CEO & Co-Founder</span>
              </p>
            </div>
          </div>
        </motion.div>
      </motion.section>

      {/* Mobile Layout */}
      <motion.section
        ref={mobileRef}
        className="relative flex w-full flex-col items-center lg:hidden"
        style={{
          backgroundColor: 'transparent',
          padding: '40px 20px 40px',
          overflow: 'hidden',
          position: 'relative',
        }}
        initial={{ opacity: 0 }}
        animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Content */}
        <div style={{
          position: 'relative',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          width: '100%',
          maxWidth: '340px',
        }}>
          {/* Card with overlaid title, mission, button */}
          <div style={{
            width: '100%',
            aspectRatio: '398 / 484',
            borderRadius: '19px',
            overflow: 'hidden',
            boxShadow: 'rgba(243, 234, 255, 0.63) 0px 0px 0px 1px inset',
            position: 'relative',
          }}>
            <img
              src={CARD_IMAGE}
              alt=""
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: 'rotate(180deg)',
                opacity: 0.72,
              }}
            />

            {/* Overlay — centered in the card */}
            <div style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
              padding: '0 16px',
            }}>
              <h1 style={{
                lineHeight: '36px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 900,
                fontSize: '32px',
                color: WHITE,
                margin: 0,
                textAlign: 'center',
                fontVariationSettings: "'wght' 900",
              }}>
                Our Story
              </h1>

              <p style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '22px',
                color: WHITE,
                margin: '8px 0 0 0',
                textAlign: 'center',
                maxWidth: '220px',
              }}>
                Our mission is to make it easy for anyone to play a game the moment they discover it.
              </p>

              <button
                onClick={handleScrollToTeam}
                style={{
                  width: '207px',
                  height: '35px',
                  borderRadius: '44px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '18px',
                  lineHeight: '35px',
                  cursor: 'pointer',
                  marginTop: '16px',
                  color: WHITE,
                  background: 'transparent',
                  border: `1px solid ${WHITE}`,
                }}
              >
                Meet the Team
              </button>
            </div>
          </div>

          {/* Meet Playcast. */}
          <h2 style={{
            lineHeight: '36px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 900,
            fontSize: '33px',
            margin: '42px 0 0 0',
            textAlign: 'left',
            width: '100%',
            fontVariationSettings: "'wght' 900",
          }}>
            <span style={{ color: WHITE }}>Meet </span>
            <span style={{ color: YELLOW }}>Playcast.</span>
          </h2>

          {/* Story Text */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', width: '100%' }}>
            <p style={{ ...textBase, fontSize: '16px', lineHeight: '24px' }}>
              Playcast exists to unify gaming and streaming into an open platform—one that restores
              the imagination, intimacy, and communal creativity that defined classic role-playing
              games long before matchmaking queues and microtransactions replaced table-sized worlds
              and shared storytelling.
            </p>
            <p style={{ ...textBase, fontSize: '16px', lineHeight: '24px' }}>
              For years, <span style={yellowBold}>the industry obsessed over the wrong problem</span>.
              Cloud gaming debates fixated on milliseconds of latency while millions of players quietly
              embraced 100% latency—watching instead of playing. The real issue wasn't performance.
              It was platforms. Gamers and developers didn't want another closed ecosystem or another
              credit-card gate. <span style={yellowBold}>They wanted open communities and shared experiences.</span>
            </p>
            <p style={{ ...textBase, fontSize: '16px', lineHeight: '24px' }}>
              Instead of adding more centralized infrastructure, Playcast takes the opposite path. It
              achieves unprecedented speed by avoiding the cloud altogether.{' '}
              <span style={whiteBold}>Playcasting is a peer-to-peer network</span> powered by the vast,
              underutilized encoding hardware already sitting inside gaming PCs shipped over the last
              decade; the future hiding in plain sight.
            </p>
            <p style={{ ...textBase, fontSize: '16px', lineHeight: '24px' }}>
              During long, dark winters, <span style={yellowBold}>imagination once mattered more than hardware.</span>{' '}
              Stories traveled across tables, not data centers. Playcast is an attempt to carry that
              spirit forward into the twenty-first century—restoring shared storytelling to a world
              that mistook scale for connection.
            </p>
            <p style={{ ...textBase, fontSize: '16px', lineHeight: '24px' }}>
              — <span style={whiteBold}>Alex St. John</span>
              <br />
              <span style={whiteBold}>CEO & Co-Founder</span>
            </p>
          </div>

        </div>
      </motion.section>
    </>
  );
};
